'use client'

import { CheckboxControl, ToggleControl, type CheckboxControlProps } from '@systemeio/components'
import { useId, type ReactNode } from 'react'

type AccessibleLabelProps =
  | {
      ariaLabel?: string
      label: ReactNode
    }
  | {
      ariaLabel: string
      label?: never
    }

type CheckboxProps = AccessibleLabelProps & {
  dataTestId?: string
  description?: ReactNode
  disabled?: boolean
  isFetching?: boolean
  onChange: (value: boolean) => void
  value: boolean
}

type CheckBoxSwitcherProps = AccessibleLabelProps & {
  dataTestId?: string
  disabled?: boolean
  onChange: (value: boolean) => void
  selected: boolean
}

function Checkbox({
  ariaLabel,
  dataTestId,
  description,
  disabled,
  isFetching,
  label,
  onChange,
  value,
}: CheckboxProps) {
  const labelId = useId()
  const hasLabel = label != null
  const accessibleNameProps = ariaLabel
    ? { 'aria-label': ariaLabel }
    : { 'aria-labelledby': labelId }
  const rootProps: NonNullable<CheckboxControlProps['rootProps']> & {
    'data-test'?: string
  } = {
    ...accessibleNameProps,
    className: isFetching ? 'animate-pulse' : undefined,
    'data-test': dataTestId,
  }

  return (
    <CheckboxControl
      checked={value}
      description={description}
      disabled={disabled}
      label={label}
      labelProps={hasLabel ? { id: labelId } : undefined}
      onCheckedChange={onChange}
      rootProps={rootProps}
    />
  )
}

function CheckBoxSwitcher({
  ariaLabel,
  dataTestId,
  disabled,
  label,
  onChange,
  selected,
}: CheckBoxSwitcherProps) {
  const labelId = useId()
  const hasLabel = label != null
  const accessibleNameProps = ariaLabel
    ? { 'aria-label': ariaLabel }
    : { 'aria-labelledby': labelId }

  return (
    <ToggleControl
      {...accessibleNameProps}
      checked={selected}
      data-test-id={dataTestId}
      disabled={disabled}
      label={label}
      labelProps={hasLabel ? { id: labelId } : undefined}
      onCheckedChange={onChange}
    />
  )
}

export { CheckBoxSwitcher, Checkbox }
export type { CheckBoxSwitcherProps, CheckboxProps }
